import * as React from 'react'
import { Button } from "@progress/kendo-react-buttons";
import { EditorUtils, Editor, EditorTools, ProseMirror } from '@progress/kendo-react-editor';
import { Schema } from '@progress/kendo-editor-common';
import { accentUtils, showDialog, t } from '../../services/HelperService';
import { AIEmailEditDlg } from '../../dialogs/AIEmailEditDlg';
import { getAccess } from '../../services/UserService';
import { AccentCodeDlg } from '../code/AccentCodeDlg';
import { AccentLabel } from '../AccentLabel';

const { EditorState, EditorView } = ProseMirror;
const { tableResizing } = EditorUtils;

const hasTextContent = html => {
    if (accentUtils.isEmpty(html)) return false;

    if (typeof document === "undefined") {
        return !accentUtils.isEmpty(String(html).replace(/<[^>]*>/g, ""));
    }

    const element = document.createElement("div");
    element.innerHTML = html;
    return !accentUtils.isEmpty(element.textContent);
};

const {
    Bold, Italic, Underline, Strikethrough, Subscript, Superscript,
    ForeColor, BackColor,
    AlignLeft, AlignCenter, AlignRight, AlignJustify,
    Indent, Outdent, OrderedList, UnorderedList,
    Undo, Redo, FontSize, FontName, FormatBlock,
    Link, Unlink, InsertImage, ViewHtml,
    InsertTable,
    TableProperties, TableCellProperties,
    AddRowBefore, AddRowAfter, AddColumnBefore, AddColumnAfter,
    DeleteRow, DeleteColumn, DeleteTable,
    MergeCells, SplitCell
} = EditorTools;

const ToggleTableColumnResize = ({ readOnly, enabled, onToggle }) => {
    return (
        <Button
            onClick={onToggle}
            disabled={readOnly}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title={enabled ? "Disable table column resizing" : "Enable table column resizing"}
        >
            {enabled ? "Resize Cols: On" : "Resize Cols: Off"}
        </Button>
    );
};


const AIEdit = ({ view, readOnly, getContext, onChange }) => {



    
    const onEdit = React.useCallback(async () => {
        try {

            const context = await getContext();

            const selectResult = await showDialog(
                <AIEmailEditDlg context={context} />
            );

            if (selectResult.canceled) return;

            onChange({ body: selectResult.body });

            
        } catch (error) {
            console.error("Error inserting image:", error);
        }
    }, [onChange, getContext]);


    return (
        <Button
            onClick={onEdit}
            disabled={readOnly}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title="AI Edit"
            icon="edit"
        />
    );
};


export const AccentHtmlUnbound = React.forwardRef((props, ref) => {

    const ctrl = React.useRef();

    const [refreshKey, setRefreshKey] = React.useState(1);

    const [value, setValue] = React.useState(props.defaultValue??"<p><p/>");
    const [hasText, setHasText] = React.useState(hasTextContent(props.defaultValue));
    const [allowTableColumnResize, setAllowTableColumnResize] = React.useState(!!props.allowTableColumnResize);



    const insertText = (value) => {

        var newState = ctrl.current.view.state.apply(ctrl.current.view.state.tr.insertText(value, ctrl.current.view.state.selection.from));
        ctrl.current.view.updateState(newState);

        var newValue = ctrl.current.view.dom.innerHTML;
        setHasText(!accentUtils.isEmpty(ctrl.current.view.dom.textContent));

        props.onChange(newValue);

 
    }

    React.useImperativeHandle(ref, () => ({
        insertText : insertText
    }), [props]);


    const onContextMenu = (e) => {

        if (!props.onContextMenu) return;

        e.preventDefault(); // Prevent default browser context menu
       
        const position = {
            left: e.pageX,
            top: e.pageY ,
        };

        props.onContextMenu(position, e);


    }

    const onEditorBlur = (_view, _domEvent) =>{

        if (!accentUtils.isNull(props.onFocusLost)) {
            props.onFocusLost({ selectionStart: _view.state.selection.from });
        }

        var newValue = ctrl.current.view.dom.innerHTML;

        if (accentUtils.isNullOrEmpty(ctrl.current.view.dom.textContent)) {
            setHasText(false);
            props.onChange(null)
        } else {
            setHasText(true);
            props.onChange(newValue);
        }

    }

    const onMount = (event) =>{
        
        event.dom.oncontextmenu = onContextMenu;

        const { plugins, doc } = event.viewProps.state;

        return new EditorView(
            { mount: event.dom }, {
            ...event.viewProps,
            state: EditorState.create({
                doc,
                plugins: allowTableColumnResize
                    ? [...tableResizing(), ...plugins]
                    : [...plugins]
            }),
            handleDOMEvents: {
                ...(event.viewProps.handleDOMEvents || {}),
                'blur': onEditorBlur
            },
            //handleKeyDown: me.onKeydown
        }
        );
    }

    const onChange = event => {
        //setValue(event.value);        

        if (props.onChange) {

            const isEmpty = accentUtils.isEmpty(event.value.textContent);
            const val = isEmpty ? null : event.html;

            setHasText(!isEmpty);
            props.onChange(val);
        }

    };

    const onAIChanged = e => {

        setValue(e.body);
        setHasText(hasTextContent(e.body));


        props.onChange(e.body);

        setRefreshKey(k => k + 1);
    }

    var tools = undefined;

    const allTools = props.allTools;
    const minTools = props.minTools;
    const height = (accentUtils.isNull(props.height)) ? 500 : props.height;

    if (allTools) {
        tools = [
            [Bold, Italic, Underline, Strikethrough],
            [Subscript, Superscript],
            [AlignLeft, AlignCenter, AlignRight, AlignJustify],
            [Indent, Outdent],
            [OrderedList, UnorderedList],
            [FontSize, FontName, FormatBlock, CustomForeColor, CustomBackColor],
            [Undo, Redo],
            [Link, Unlink, InsertImage, CustomHtmlEditor],
            [InsertTable],
            [TableProperties, TableCellProperties],
            [AddRowBefore, AddRowAfter, AddColumnBefore, AddColumnAfter],
            [DeleteRow, DeleteColumn, DeleteTable],
            [MergeCells, SplitCell]
        ];
    } else {

        if (minTools) {
            tools =
                [
                    [Bold, Italic, Underline, Strikethrough],
                    [OrderedList, UnorderedList],
                    [Undo]
                ];

        } else {

            tools =
                [
                    [Bold, Italic, Underline, Strikethrough],
                    [Subscript, Superscript],
                    [AlignLeft, AlignCenter, AlignRight, AlignJustify],
                    [Indent, Outdent],
                    [OrderedList, UnorderedList],
                    FontSize, FontName, FormatBlock,
                    [Undo, Redo],
                    [Link, Unlink]
                ];

        }
    }

    if (props.showAI && props.getAIContext && getAccess().modules().RTL_AI()) {
        tools.push([p => <AIEdit {...p} getContext={props.getAIContext} onChange={onAIChanged} />]);
    }

    if (allTools) {
        tools.push([p => (
            <ToggleTableColumnResize
                {...p}
                enabled={allowTableColumnResize}
                onToggle={() => {
                    // Preserve the current HTML before remounting (Editor uses defaultContent).
                    try {
                        const html = ctrl.current?.view?.dom?.innerHTML;
                        if (!accentUtils.isNull(html)) {
                            setValue(html);
                        }
                    } catch { }

                    setAllowTableColumnResize(v => !v);
                    setRefreshKey(k => k + 1);
                }}
            />
        )]);
    }


    const isValid = !props.required || hasText;
    const className = isValid ? "accentHtml" : "accentHtml invalid";
    const editor = <Editor
        ref={ctrl}
        key={refreshKey}
        defaultContent={ value}
        defaultEditMode={props.isIframe ? undefined : "div"}
        onChange={onChange}
        tools={tools} // or your tools config
        onMount={onMount}
        contentStyle={{ height: height }}
        onContextMenu={onContextMenu}
    />;

    return (<div className={className} data-tagname={props.dataTagName}>
        {accentUtils.isEmpty(props.label) ? editor : <AccentLabel text={t(props.label)}>{editor}</AccentLabel>}
    </div>);
});

const CustomForeColor = props => (
    <ForeColor {...props} colorPickerProps={{ view: 'gradient' }} />
);
const CustomBackColor = props => (
    <BackColor {...props} colorPickerProps={{ view: 'gradient' }} />
);



const CustomHtmlEditor = ({ view }) => {
    const onClick = async () => {
        const res = await showDialog(<AccentCodeDlg code={EditorUtils.getHtml(view.state)} />);
        if (res.canceled) return;

        EditorUtils.setHtml(view, res.code);
        
    };

    return (
        <Button
            onClick={onClick}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title="View Html"
            icon="code"
        />
    );
};


